import React, { useState, useEffect } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { Loader2 } from "lucide-react";

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Textarea } from "../ui/textarea";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const ResellerSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Nome muito curto")
		.max(80, "Nome muito longo")
		.required("Informe o nome"),
	email: Yup.string().email("E-mail inválido"),
	commissionPercent: Yup.number()
		.min(0, "Mínimo 0")
		.max(100, "Máximo 100"),
});

const ResellerModal = ({ open, onClose, reseller }) => {
	const initialState = {
		name: "",
		email: "",
		phone: "",
		document: "",
		commissionPercent: 0,
		isActive: true,
		notes: "",
	};

	const [dados, setDados] = useState(initialState);

	useEffect(() => {
		if (!open) {
			setDados(initialState);
			return;
		}

		if (reseller) {
			setDados({
				...initialState,
				...reseller,
				email: reseller.email || "",
				phone: reseller.phone || "",
				document: reseller.document || "",
				notes: reseller.notes || "",
				commissionPercent: Number(reseller.commissionPercent || 0),
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [reseller, open]);

	const salvar = async (valores) => {
		const corpo = {
			...valores,
			commissionPercent: Number(valores.commissionPercent) || 0,
		};

		try {
			if (reseller?.id) {
				await api.put(`/resellers/${reseller.id}`, corpo);
			} else {
				await api.post("/resellers", corpo);
			}
			toast.success(
				reseller?.id ? "Representante atualizado." : "Representante criado."
			);
			onClose();
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<Dialog open={open} onOpenChange={(v) => !v && onClose()}>
			<DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>
						{reseller?.id ? "Editar representante" : "Novo representante"}
					</DialogTitle>
				</DialogHeader>

				<Formik
					initialValues={dados}
					enableReinitialize
					validationSchema={ResellerSchema}
					onSubmit={async (valores, actions) => {
						await salvar(valores);
						actions.setSubmitting(false);
					}}
				>
					{({ values, touched, errors, isSubmitting, setFieldValue }) => (
						<Form className="space-y-4">
							<div className="space-y-1.5">
								<Label htmlFor="name">Nome</Label>
								<Field as={Input} id="name" name="name" />
								{touched.name && errors.name && (
									<p className="text-xs text-destructive">{errors.name}</p>
								)}
							</div>

							<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
								<div className="space-y-1.5">
									<Label htmlFor="email">E-mail</Label>
									<Field as={Input} id="email" name="email" type="email" />
									{touched.email && errors.email && (
										<p className="text-xs text-destructive">{errors.email}</p>
									)}
								</div>

								<div className="space-y-1.5">
									<Label htmlFor="phone">Telefone</Label>
									<Field as={Input} id="phone" name="phone" />
								</div>

								<div className="space-y-1.5">
									<Label htmlFor="document">CPF / CNPJ</Label>
									<Field as={Input} id="document" name="document" />
								</div>

								<div className="space-y-1.5">
									<Label htmlFor="commissionPercent">Comissão (%)</Label>
									<Field
										as={Input}
										id="commissionPercent"
										name="commissionPercent"
										type="number"
										step="0.01"
									/>
									<p className="text-xs text-muted-foreground">
										Sobre o que as empresas dele pagarem.
									</p>
									{touched.commissionPercent && errors.commissionPercent && (
										<p className="text-xs text-destructive">
											{errors.commissionPercent}
										</p>
									)}
								</div>
							</div>

							<div className="space-y-1.5">
								<Label htmlFor="notes">Observações</Label>
								<Field as={Textarea} id="notes" name="notes" rows={3} />
							</div>

							<div className="flex items-center justify-between rounded-lg border p-3">
								<div>
									<Label htmlFor="isActive">Representante ativo</Label>
									<p className="text-xs text-muted-foreground">
										Inativo não recebe novas empresas.
									</p>
								</div>
								<Switch
									id="isActive"
									checked={values.isActive}
									onCheckedChange={(v) => setFieldValue("isActive", v)}
								/>
							</div>

							<DialogFooter>
								<Button type="button" variant="outline" onClick={onClose}>
									Cancelar
								</Button>
								<Button type="submit" disabled={isSubmitting}>
									{isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
									{reseller?.id ? "Salvar" : "Criar representante"}
								</Button>
							</DialogFooter>
						</Form>
					)}
				</Formik>
			</DialogContent>
		</Dialog>
	);
};

export default ResellerModal;
